import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftComponents/SoftBox';
import SoftTypography from 'components/SoftComponents/SoftTypography';

import { getAllDevices } from 'services/api/bks';

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";
import { getUserAccessLevel } from 'utils/setup';

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

const BATTERY_THRESHOLD = 25;

function setDeviceFeatures(device) {

    var features = [
        {
            type: "Feature",
            properties: {
                name: device.name,
                uuid: device.uuid,
                serial_no: device.serial_number,
                site_id: device.stop.site_id
            },
            geometry: {
                type: 'Point',
                coordinates: device.position
            }
        }
    ];

    return features;
}

function Map({ selected_group, parent_group = {}, allow_devices = false, colour = null, settings, padding, user_type = "viewer" }) {

    const mapContainer = useRef(null);
    const map = useRef(null);

    const [opacity, setOpacity] = useState(0);

    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
        if (getUserAccessLevel(user_type, 'technician')) {
            description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }

    const handleMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        popup.remove();
    }

    useEffect(() => {

        // console.log(map)
        if (map.current) {
            return;
        }

        var bounds = bbox({
            "type": "FeatureCollection",
            "features": selected_group.geojson
        });

        var polygon = bboxPolygon(bounds);

        // console.log(polygon)
        var map_settings = settings;
        map_settings.container = mapContainer.current;
        map_settings.interactive = false;
        map_settings.center = center({
            "type": "FeatureCollection",
            "features": selected_group.geojson
        }).geometry.coordinates;

        map.current = new mapboxgl.Map(map_settings);

        map.current.on('load', () => {

            var existing_online_image = map.current.hasImage('cameraIconOnline');
            var existing_warning_image = map.current.hasImage('cameraIconWarning');
            var existing_offline_image = map.current.hasImage('cameraIconOffline');

            map.current.loadImage(
                cameraOnline, (error, image) => {
                    if (error) throw error;
                    if (!existing_online_image) {
                        map.current.addImage('cameraIconOnline', image);
                    }
                }
            );
            map.current.loadImage(
                cameraWarning, (error, image) => {
                    if (error) throw error;
                    if (!existing_warning_image) {
                        map.current.addImage('cameraIconWarning', image);
                    }
                }
            );
            map.current.loadImage(
                cameraOffline, (error, image) => {
                    if (error) throw error;
                    if (!existing_offline_image) {
                        map.current.addImage('cameraIconOffline', image);
                    }
                }
            );

            map.current.addSource('geojson', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": selected_group.geojson
                }
            });

            if (typeof selected_group.parent_id != 'undefined') {
                map.current.addLayer({
                    id: 'geojson',
                    type: 'line',
                    source: 'geojson',
                    paint: {
                        "line-color": colour,
                        "line-width": 4,
                    }
                });
            }
            else {
                map.current.addLayer({
                    id: 'geojson',
                    type: 'fill',
                    source: 'geojson',
                    paint: {
                        "fill-opacity": 0.2,
                        "fill-color": colour
                    }
                }).addLayer({
                    id: 'geojson-border',
                    type: 'line',
                    source: 'geojson',
                    paint: {
                        "line-color": colour,
                        "line-width": 2
                    }
                });
            }

            if (typeof parent_group.group_id != 'undefined') {
                map.current.addSource('geojson-parent', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": parent_group.geojson
                    }
                })

                map.current.addLayer({
                    id: 'geojson-parent',
                    type: 'fill',
                    source: 'geojson-parent',
                    paint: {
                        "fill-opacity": 0.1,
                        "fill-color": "black"
                    }
                }).addLayer({
                    id: 'geojson-border-parent',
                    type: 'line',
                    source: 'geojson-parent',
                    paint: {
                        "line-color": "black",
                        "line-width": 2,
                        "line-opacity": 0.2
                    }
                });
            }

            if (allow_devices) {
                selected_group.devices.forEach(function (device) {

                    if (device.active) {
                        if (['1003', '1004', '1005', '1006', '1007', 'Mercury Carpark (1266)'].indexOf(device.name) > -1 || selected_group.type == "camera") {
                            if (device.connected_state == 'offline') {
                                var icon_image = 'cameraIconOffline';
                            }
                            else if (device.battery <= BATTERY_THRESHOLD && getUserAccessLevel(user_type, 'technician')) {
                                var icon_image = 'cameraIconWarning';
                            }
                            else {
                                var icon_image = 'cameraIconOnline';
                            }

                            // if (!existing_device_source) {
                                map.current.addSource('device-' + device.uuid, {
                                    type: 'geojson',
                                    data: {
                                        type: 'FeatureCollection',
                                        features: setDeviceFeatures(device)
                                    }
                                });
                            // }

                            // if (!existing_device_layer) {
                                map.current.addLayer({
                                    id: 'device-' + device.uuid,
                                    type: 'symbol',
                                    source: 'device-' + device.uuid,
                                    layout: {
                                        'icon-image': icon_image,
                                        'icon-size': 0.044
                                    }
                                });
                            // }
                        }
                        else {
                            var device_colour = "grey";

                            if (!device.active && getUserAccessLevel(user_type, 'technician')) {
                                device_colour = '#555555';
                            }
                            else {
                                if (device.connected_state != "online") {
                                    device_colour = "#ff667c";
                                }
                                else if (device.battery <= BATTERY_THRESHOLD && getUserAccessLevel(user_type, 'technician')) {
                                    device_colour = "#edaa00";
                                }
                                else {
                                    device_colour = "#58ba2d";
                                }
                            }

                            map.current.addSource('device-' + device.uuid, {
                                type: 'geojson',
                                data: {
                                    "type": "FeatureCollection",
                                    "features": setDeviceFeatures(device)
                                }
                            });

                            map.current.addLayer({
                                id: 'device-' + device.uuid,
                                type: 'circle',
                                source: 'device-' + device.uuid,
                                paint: {
                                    'circle-color': device_colour,
                                    'circle-radius': 9,
                                    'circle-stroke-width': 3,
                                    'circle-stroke-color': '#ffffff'
                                }
                            });
                        }

                        map.current.on('mouseenter', 'device-' + device.uuid, handleMouseEnter);
                        map.current.on('mouseleave', 'device-' + device.uuid, handleMouseLeave);
                    }
                });
            }

            map.current.fitBounds(polygon.bbox, { linear: true, padding: padding, duration: 200 });
            setOpacity(1);
        });
    }, []);

    useEffect(() => () => {
        if (map.current) {
            map.current.remove();
        }
        popup.remove();

    }, []);

    return (
        <Box component="div" ref={mapContainer} sx={{ height: '100%', opacity }} />
    );
}

Map.propTypes = {
    selected_group: PropTypes.object,
    parent_group: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    allow_devices: PropTypes.bool,
    colour: PropTypes.string,
    settings: PropTypes.object,
    padding: PropTypes.number,
    user_type: PropTypes.string
};

export default Map;